import React, { useState } from "react";
import Modal from "../utils/Modal/Modal";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as LockIcon } from "../../assets/icons/lock.svg";
import { ReactComponent as StarIcon } from "../../assets/icons/star.svg";

import {
  ModalContainer,
  PromptCloseButton,
  PromptInputContainer,
  PromptInputTitle,
  PromptModalButtons,
  PromptModalHeader,
  PromptModalTitle,
  PromptSeedInput,
  PromptTextarea,
  Buttons,
  Button,
  PromptRow,
  Badge,
  RelativeContainer,
  SummaryContainer,
  EstTimeContainer,
  EstTimeText,
  CancelButton,
  ConfirmButton,
} from "../EnterPromptModal/EnterPromptModal.styles";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";
import { PRO_PLAN_NAME, PREMIUM_PLAN_NAME } from "../../utils/constants";
import UnlockModal from "../UnlockModal/UnlockModal";

const QUALITY_OPTIONS = [
  { id: "LOW", label: "Low", extraCost: 0 },
  { id: "HIGH", label: "High", extraCost: 5 },
  { id: "ULTRA", label: "Ultra", extraCost: 10 },
];

const CreateImageSettingsModal = ({
  isOpen,
  onClose,
  seedNumber,
  setSeedNumber,
  negativePrompt,
  setNegativePrompt,
  quality,
  setQuality,
  totalPrice,
  collectionSeed,
  resetSeed,
  overlayZIndex = 3500,
}) => {
  const user = useSelector(selectUser);
  const [openUnlockModal, setOpenUnlockModal] = useState(false);
  const [seedMode, setSeedMode] = React.useState(() => {
    return localStorage.getItem("seedMode") || "random";
  });

  const isProEnabled =
    [PRO_PLAN_NAME, PREMIUM_PLAN_NAME].includes(user?.subscription?.name) &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const generateRandomSeed = () => {
    return Math.floor(Math.random() * 10000000) + 1;
  };

  React.useEffect(() => {
    const savedSeedMode = localStorage.getItem("seedMode");
    const savedCustomSeed = localStorage.getItem("customSeed");

    if (savedSeedMode) {
      setSeedMode(savedSeedMode);

      if (savedSeedMode === "random") {
        setSeedNumber(generateRandomSeed());
        return;
      }

      if (savedSeedMode === "custom") {
        if (savedCustomSeed) {
          setSeedNumber(Number(savedCustomSeed));
        } else {
          setSeedNumber(collectionSeed || generateRandomSeed());
        }
        return;
      }
    }

    if (collectionSeed) {
      setSeedNumber(collectionSeed);
    } else {
      setSeedNumber(generateRandomSeed());
    }
  }, [seedMode, setSeedNumber, collectionSeed, resetSeed]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customOverlayStyles={{ zIndex: overlayZIndex }}
    >
      <ModalContainer>
        <PromptModalHeader>
          <PromptModalTitle>Customize Scene</PromptModalTitle>
          <PromptCloseButton onClick={onClose}>
            <CloseIcon />
          </PromptCloseButton>
        </PromptModalHeader>

        <PromptInputContainer>
          <PromptInputTitle>Seed</PromptInputTitle>
          <PromptRow>
            <Buttons>
              <Button
                $active={seedMode === "random"}
                onClick={() => {
                  setSeedMode("random");
                  localStorage.setItem("seedMode", "random");
                  setSeedNumber(generateRandomSeed());
                }}
              >
                Random
              </Button>
              <Button
                $active={seedMode === "custom"}
                onClick={() => {
                  setSeedMode("custom");
                  localStorage.setItem("seedMode", "custom");
                }}
              >
                Custom
              </Button>
            </Buttons>
            <PromptSeedInput
              type="number"
              placeholder="Seed..."
              value={seedNumber}
              min={1}
              max={9007199254740991}
              disabled={seedMode === "random"}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value >= 0 && value <= 9007199254740991) {
                  setSeedNumber(value > 0 ? value : 1);
                  if (seedMode === "custom") {
                    localStorage.setItem("customSeed", value > 0 ? value : 1);
                  }
                }
              }}
            />
          </PromptRow>
        </PromptInputContainer>

        <PromptInputContainer>
          <PromptInputTitle>Quality</PromptInputTitle>
          <Buttons>
            {QUALITY_OPTIONS.map((option) => {
              const isHigh = option.id === "high";
              const isEnabled = isHigh ? isProEnabled : true;

              return (
                <Button
                  key={option.id}
                  $active={quality === option.id}
                  $disabled={!isEnabled}
                  onClick={() => {
                    if (isEnabled) {
                      setQuality(option.id);
                      localStorage.setItem("createImageQuality", option.id);
                    } else {
                      setOpenUnlockModal(true);
                    }
                  }}
                >
                  {option.label}
                  {isHigh && !isProEnabled && (
                    <Badge $pro>
                      <LockIcon />
                      Pro
                    </Badge>
                  )}
                </Button>
              );
            })}
          </Buttons>
        </PromptInputContainer>

        <PromptInputContainer>
          <PromptInputTitle>Negative Prompt</PromptInputTitle>
          <RelativeContainer>
            <PromptTextarea
              placeholder="Enter Negative Prompt"
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
            />
          </RelativeContainer>
        </PromptInputContainer>

        <SummaryContainer>
          <EstTimeContainer>
            <EstTimeText>Cost:</EstTimeText>
            <StarIcon />
            <EstTimeText $bold>{totalPrice}</EstTimeText>
          </EstTimeContainer>
        </SummaryContainer>

        <PromptModalButtons>
          <ConfirmButton $type="primary" onClick={() => onClose(false)}>
            Save
          </ConfirmButton>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
        </PromptModalButtons>
      </ModalContainer>

      <UnlockModal
        isOpen={openUnlockModal}
        onClose={() => setOpenUnlockModal(false)}
      />
    </Modal>
  );
};

export default CreateImageSettingsModal;
